/**
 * PersonaDTO - Data Transfer Object for Persona transformations
 *
 * Handles all transformations between frontend persona format and v1 API format.
 * Uses immutable readonly properties following Google TypeScript style guide.
 *
 * @see PERSONAS_VOICE_API_MIGRATION_GUIDE.md
 * @see personas-v1.yaml
 */

import type { Persona, PersonaMetadata } from "@/types/api/Persona"
import {
  clampVoiceSpeed,
  clampVoiceVolume,
  VOICE_SPEED_DEFAULT,
  VOICE_VOLUME_DEFAULT,
} from "@/types/ModelConfigs/CovalVoiceConstants"

/**
 * v1 API request format for creating a persona
 */
interface V1CreatePersonaRequest {
  readonly name: string
  readonly persona_prompt: string | null
  readonly voice_name: string
  readonly language_code: string
  readonly background_sound: string | null
  readonly background_sound_volume: number | null
  readonly voice_volume?: number
  readonly voice_speed?: number
  readonly wait_seconds: number | null
  readonly conversation_initiation: "speak_first" | "wait_for_user" | null
}

/**
 * v1 API request format for updating a persona (all fields optional except persona_id)
 */
interface V1UpdatePersonaRequest {
  readonly persona_id: string
  readonly name?: string
  readonly persona_prompt?: string | null
  readonly voice_name?: string
  readonly language_code?: string
  readonly background_sound?: string | null
  readonly background_sound_volume?: number | null
  readonly voice_volume?: number | null
  readonly voice_speed?: number | null
  readonly wait_seconds?: number | null
  readonly conversation_initiation?: "speak_first" | "wait_for_user" | null
}

/**
 * Frontend form data format
 */
export interface PersonaFormData {
  readonly display_name: string
  readonly simulated_user_prompt?: string
  readonly voice_name: string
  readonly language_code: string
  readonly metadata?: PersonaMetadata
}

/**
 * Static utility class for persona data transformations.
 * Uses private constructor to prevent instantiation (namespace pattern).
 */
export class PersonaDTO {
  private constructor() {
    // Prevent instantiation
  }

  /**
   * Transforms frontend form data to v1 API create request format
   *
   * @param formData - Frontend form data
   * @returns v1 API create request object
   */
  static toV1CreateRequest(formData: PersonaFormData): V1CreatePersonaRequest {
    const metadata = formData.metadata || {}
    const voiceVolume = clampVoiceVolume(metadata.voice_volume)
    const voiceSpeed = clampVoiceSpeed(
      metadata.voice_speed,
      formData.voice_name,
    )

    return {
      name: formData.display_name,
      persona_prompt: formData.simulated_user_prompt || null,
      voice_name: formData.voice_name,
      language_code: formData.language_code,
      background_sound: metadata.background_sound || null,
      background_sound_volume: metadata.background_sound_volume ?? null,
      ...(voiceVolume !== undefined &&
        voiceVolume !== VOICE_VOLUME_DEFAULT && {
          voice_volume: voiceVolume,
        }),
      ...(voiceSpeed !== undefined &&
        voiceSpeed !== VOICE_SPEED_DEFAULT && {
          voice_speed: voiceSpeed,
        }),
      wait_seconds: metadata.wait_seconds ?? null,
      conversation_initiation: PersonaDTO.mapConversationInitiator(
        metadata.conversation_initiator,
      ),
    }
  }

  /**
   * Transforms frontend form data to v1 API update request format
   *
   * @param personaId - ID of persona to update
   * @param formData - Frontend form data (partial update)
   * @returns v1 API update request object
   */
  static toV1UpdateRequest(
    personaId: string,
    formData: Partial<PersonaFormData>,
  ): V1UpdatePersonaRequest {
    const metadata = formData.metadata || {}
    const voiceVolume = clampVoiceVolume(metadata.voice_volume)
    const resolvedVoiceName = formData.voice_name ?? metadata.voice_name
    const voiceSpeed = clampVoiceSpeed(metadata.voice_speed, resolvedVoiceName)
    const hasVoiceVolumeKey =
      formData.metadata !== undefined &&
      Object.hasOwn(formData.metadata, "voice_volume")
    const hasVoiceSpeedKey =
      formData.metadata !== undefined &&
      Object.hasOwn(formData.metadata, "voice_speed")

    return {
      persona_id: personaId,
      ...(formData.display_name !== undefined && {
        name: formData.display_name,
      }),
      ...(formData.simulated_user_prompt !== undefined && {
        persona_prompt: formData.simulated_user_prompt || null,
      }),
      ...(formData.voice_name !== undefined && {
        voice_name: formData.voice_name,
      }),
      ...(formData.language_code !== undefined && {
        language_code: formData.language_code,
      }),
      ...(metadata.background_sound !== undefined && {
        background_sound: metadata.background_sound || null,
      }),
      ...(metadata.background_sound_volume !== undefined && {
        background_sound_volume: metadata.background_sound_volume ?? null,
      }),
      ...(hasVoiceVolumeKey && {
        voice_volume:
          voiceVolume === undefined || voiceVolume === VOICE_VOLUME_DEFAULT
            ? null
            : voiceVolume,
      }),
      ...(hasVoiceSpeedKey && {
        voice_speed:
          voiceSpeed === undefined || voiceSpeed === VOICE_SPEED_DEFAULT
            ? null
            : voiceSpeed,
      }),
      ...(metadata.wait_seconds !== undefined && {
        wait_seconds: metadata.wait_seconds ?? null,
      }),
      ...(metadata.conversation_initiator !== undefined && {
        conversation_initiation: PersonaDTO.mapConversationInitiator(
          metadata.conversation_initiator,
        ),
      }),
    }
  }

  /**
   * Detects if a persona uses the legacy ElevenLabs format
   *
   * Legacy personas have voice_id in metadata but no voice_name.
   * These personas must select a new voice/language before updating.
   *
   * @param persona - Persona to check
   * @returns true if persona uses legacy format
   */
  static isLegacy(persona: Persona): boolean {
    const metadata = persona.metadata

    // Check if voice_name exists either as a top-level column or in metadata
    const hasVoiceName = persona.voice_name || metadata?.voice_name

    return (
      metadata?.voice_id !== undefined &&
      metadata?.voice_id !== null &&
      !hasVoiceName
    )
  }

  /**
   * Maps frontend conversation initiator to v1 API format
   *
   * @param initiator - Frontend initiator value
   * @returns v1 API conversation_initiation value
   */
  private static mapConversationInitiator(
    initiator?: string,
  ): "speak_first" | "wait_for_user" | null {
    if (!initiator) {
      return null
    }

    switch (initiator.toLowerCase()) {
      case "agent":
      case "persona":
        return "speak_first"
      case "user":
        return "wait_for_user"
      default:
        return null
    }
  }

  /**
   * Maps v1 API conversation_initiation back to frontend format
   *
   * @param initiation - v1 API initiation value
   * @returns Frontend conversation_initiator value
   */
  static fromV1ConversationInitiation(
    initiation?: "speak_first" | "wait_for_user" | null,
  ): string | null {
    if (!initiation) {
      return null
    }

    switch (initiation) {
      case "speak_first":
        return "agent"
      case "wait_for_user":
        return "user"
      default:
        return null
    }
  }
}
